// The data behind the Entity summaries help page: each core entity's boundary and the map that links them.
import {
  Archive,
  Bot,
  Brain,
  Briefcase,
  FileText,
  GitBranch,
  GitFork,
  Gavel,
  Layers3,
  Library,
  ListChecks,
  MessageSquare,
  Route,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import type { ArchitectureTone } from './ArchitectureGraph.js';

export type EntityTone = ArchitectureTone;

export interface EntitySummary {
  id: string;
  name: string;
  category: string;
  icon: LucideIcon;
  tone: EntityTone;
  summary: string;
  owns: string[];
  notFor: string[];
  relationships: string[];
}

export const ENTITIES: EntitySummary[] = [
  {
    id: 'project',
    name: 'Project',
    category: 'workspace',
    icon: Briefcase,
    tone: 'frontend',
    summary:
      'Durable workspace for building, operating, improving, or researching a bounded business, product, or system effort.',
    owns: ['ProjectThreads', 'ProjectChanges', 'project outputs', 'project memory', 'project decisions'],
    notFor: ['runtime business data', 'raw generated client data', 'hidden chat-only state'],
    relationships: [
      'Groups conversations, tasks, workflows, workforce, artifacts, memories, decisions, and library adoptions.',
    ],
  },
  {
    id: 'project-thread',
    name: 'ProjectThread',
    category: 'workspace',
    icon: GitFork,
    tone: 'frontend',
    summary: 'A bounded work conversation inside a project that can carry multiple related ProjectChanges.',
    owns: ['thread narrative', 'active work mode', 'linked resources', 'one or more ProjectChanges'],
    notFor: ['being the execution lane itself', 'replacing ProjectChange review state'],
    relationships: [
      'Feeds and organizes changes, artifacts, tasks, workflows, workstreams, workforce routes, and decisions.',
    ],
  },
  {
    id: 'project-change',
    name: 'ProjectChange',
    category: 'workspace',
    icon: GitBranch,
    tone: 'frontend',
    summary: 'Branch-like unit of functionality, refactor, fix, support, launch, or expansion work.',
    owns: ['change lifecycle', 'readiness', 'review state', 'work mode', 'active execution lanes'],
    notFor: ['client runtime records', 'durable output payloads', 'replacing WorkOutput'],
    relationships: ['Owns workstreams and receives named outputs from tasks, workflows, conversations, and workforce.'],
  },
  {
    id: 'conversation',
    name: 'Conversation',
    category: 'interaction',
    icon: MessageSquare,
    tone: 'bus',
    summary: 'User-facing continuity layer where work is discovered, discussed, routed, refined, and audited.',
    owns: ['messages', 'assistant context', 'intent entry', 'clarification', 'conversation attachments'],
    notFor: ['the work item itself', 'silent production changes', 'untracked durable outputs'],
    relationships: [
      'Can create or refine projects, threads, changes, tasks, workflows, artifacts, decisions, and workforce plans.',
    ],
  },
  {
    id: 'action-request',
    name: 'Action Request',
    category: 'attention',
    icon: Route,
    tone: 'security',
    summary:
      'Universal intervention entry point for something that needs attention, routing, repair, judgment, or closure.',
    owns: ['reason', 'evidence', 'severity', 'instructions', 'status timeline', 'audit trail'],
    notFor: ['being a task by default', 'executing work directly', 'workflow pause/resume'],
    relationships: [
      'Can create or link tasks, decisions, workflow drafts/versions, workforce routes, workstreams, and log-only outcomes.',
    ],
  },
  {
    id: 'task',
    name: 'Task',
    category: 'accountability',
    icon: ListChecks,
    tone: 'backend',
    summary: 'Accountable bounded work item with an owner, goal, expected output, budget, deadline, and result.',
    owns: ['assignment', 'status lifecycle', 'input/output contracts', 'approval gates', 'result artifact link'],
    notFor: ['hidden multi-step orchestration', 'unbounded collaboration', 'universal routing'],
    relationships: ['May be spawned by conversations, projects, workflows, daemons, artifacts, or Action Requests.'],
  },
  {
    id: 'workflow',
    name: 'Workflow',
    category: 'procedure',
    icon: Workflow,
    tone: 'backend',
    summary:
      'Reusable multi-step procedure for known sequences, repeatable operations, validation, gates, and output routing.',
    owns: ['step graph', 'contracts', 'processors', 'human gates', 'runs', 'versions', 'start/stop lifecycle'],
    notFor: ['being the actor', 'being the ticket', 'pause/resume semantics'],
    relationships: [
      'Coordinates templates, tools, processors, tasks, workforce, daemons, artifacts, memory, and decisions.',
    ],
  },
  {
    id: 'workstream',
    name: 'Workstream',
    category: 'collaboration',
    icon: Layers3,
    tone: 'bus',
    summary: 'Active execution lane under a ProjectChange for multi-actor or iterative specialist collaboration.',
    owns: ['dynamic members', 'Project Manager actor', 'event routing', 'specialist coordination'],
    notFor: ['default ticketing', 'direct ProjectThread ownership', 'single bounded one-shot tasks'],
    relationships: ['Belongs to one ProjectChange and links back to the ProjectThread through that change.'],
  },
  {
    id: 'template',
    name: 'Template',
    category: 'capability',
    icon: FileText,
    tone: 'database',
    summary:
      'Reusable specialist capability or persona used by conversations, tasks, workflow steps, workforce, and daemons.',
    owns: ['goal', 'instructions', 'constraints', 'tools', 'model requirements', 'permissions', 'quality metadata'],
    notFor: ['actor identity', 'run state', 'owning every runtime contract', 'silent upgrades'],
    relationships: [
      'Consumers pin versions; upgrades require impact, evaluation evidence, and a human or policy adoption gate.',
    ],
  },
  {
    id: 'workforce-daemon',
    name: 'Workforce / Daemon',
    category: 'actor',
    icon: Bot,
    tone: 'cloud',
    summary: 'Long-lived actors. Workforce serves org/customer work; daemons serve internal system maintenance.',
    owns: ['identity', 'mandate', 'scope', 'permissions', 'template binding', 'reporting', 'supervision'],
    notFor: [
      'mixing system and customer trust boundaries',
      'opaque autonomous magic',
      'direct production self-modification',
    ],
    relationships: [
      'Executes through engine-owned semantics and worker envelopes; improvement proposals route through Action Requests.',
    ],
  },
  {
    id: 'artifact',
    name: 'Artifact',
    category: 'output',
    icon: Archive,
    tone: 'database',
    summary: 'Durable deliverable such as a plan, report, design, workflow draft, code output, or evaluation result.',
    owns: ['rendered content', 'version history', 'source provenance', 'downstream consumer links'],
    notFor: ['ephemeral chat text', 'hidden result payloads', 'unversioned reusable output'],
    relationships: [
      'Produced by conversations, tasks, workflows, workforce, daemons, catalog promotions, and code generation.',
    ],
  },
  {
    id: 'memory',
    name: 'Memory',
    category: 'knowledge',
    icon: Brain,
    tone: 'database',
    summary: 'Scoped fact, preference, lesson, or knowledge item used for future context and continuity.',
    owns: ['scope', 'source', 'confidence', 'promotion state', 'retrieval context'],
    notFor: ['audit decisions', 'artifact storage', 'unscoped global facts'],
    relationships: ['Can be scoped to conversation, task, project, actor, daemon/system, or organization.'],
  },
  {
    id: 'decision',
    name: 'Decision',
    category: 'judgment',
    icon: Gavel,
    tone: 'security',
    summary: 'Approval, judgment, classification, gate, routing choice, rejection, promotion, rollback, or rationale.',
    owns: ['actor', 'rationale', 'payload', 'source link', 'approval/rejection state', 'audit'],
    notFor: ['executing work itself', 'hiding permission grants', 'raw status-only rows'],
    relationships: [
      'Can approve, reject, route, close an Action Request, create tasks, or request workflow/workstream changes.',
    ],
  },
  {
    id: 'catalog-library',
    name: 'Catalog / Library',
    category: 'adoption',
    icon: Library,
    tone: 'external',
    summary:
      'Reviewed source-backed ecosystem for models, tools, templates, workflows, evaluations, and reusable capabilities.',
    owns: ['source provenance', 'candidate review', 'promoted versions', 'adoption references', 'stale indicators'],
    notFor: ['invented records without sources', 'unreviewed marketplace adoption', 'runtime execution state'],
    relationships: [
      'Upgrade Workforce employees discover candidates, evaluate impact, and propose Action Requests before adoption.',
    ],
  },
];

export const FLOW = [
  { label: 'Conversation', x: 70, y: 88, tone: 'bus' as EntityTone },
  { label: 'Project', x: 250, y: 88, tone: 'frontend' as EntityTone },
  { label: 'ProjectChange', x: 430, y: 88, tone: 'frontend' as EntityTone },
  { label: 'Workstream', x: 610, y: 88, tone: 'bus' as EntityTone },
  { label: 'Task', x: 250, y: 238, tone: 'backend' as EntityTone },
  { label: 'Workflow', x: 430, y: 238, tone: 'backend' as EntityTone },
  { label: 'Workforce', x: 610, y: 238, tone: 'cloud' as EntityTone },
  { label: 'Template', x: 790, y: 238, tone: 'database' as EntityTone },
  { label: 'Action Request', x: 430, y: 388, tone: 'security' as EntityTone },
  { label: 'Artifact', x: 250, y: 538, tone: 'database' as EntityTone },
  { label: 'Memory', x: 430, y: 538, tone: 'database' as EntityTone },
  { label: 'Decision', x: 610, y: 538, tone: 'security' as EntityTone },
  { label: 'Catalog', x: 790, y: 538, tone: 'external' as EntityTone },
];

export type FlowItem = (typeof FLOW)[number];

export const EDGES = [
  ['Conversation', 'Project'],
  ['Project', 'ProjectChange'],
  ['ProjectChange', 'Workstream'],
  ['Conversation', 'Task'],
  ['Task', 'Workflow'],
  ['Workflow', 'Workforce'],
  ['Workforce', 'Template'],
  ['Workflow', 'Action Request'],
  ['Workforce', 'Action Request'],
  ['Action Request', 'Task'],
  ['Action Request', 'Decision'],
  ['Task', 'Artifact'],
  ['Workflow', 'Artifact'],
  ['Artifact', 'Memory'],
  ['Decision', 'Catalog'],
  ['Catalog', 'Template'],
];
